/**
 * BuilderChat — the pre-conversation builder route (`/:orgId/build`).
 *
 * Covers the window BEFORE a conversation exists: the user has said "build me
 * an automation" but has not sent a message yet, so there is no conversation
 * and no object. Both arrive from the first message — the conversation from
 * `useStartNewConversation`, the object's id later still, minted when the agent
 * forms its proposal.
 *
 * This is a sibling route to `ConversationsHome` rather than a variant of it.
 * They share the composer (`HeroChatInput`) and its entire state and submit
 * lifecycle (`useNewConversationComposer`); they differ only in chrome. The
 * hero screen wraps the composer in a logo, a heading, suggestions and recent
 * chats; this wraps it in the object panel and keeps the object in focus.
 *
 * On submit, `useStartNewConversation` creates the conversation — handing the
 * entity type and record from this route's query string to the conversation
 * row, where they become the source of truth — and navigates to
 * `/build/:conversationId`, which `ReasoningChat` renders in the same two-pane
 * layout. From the user's point of view the panel never moves; only the left
 * column gains a message thread. No query params travel, because from here on
 * nothing needs them.
 */

import { useCallback, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { getEntityTypeLabel } from '@/components/csv-import/utils/entityTypeLabels';
import { BuilderChatHeader } from '@/components/reasoning/BuilderChatHeader';
import { BuilderPanel } from '@/components/reasoning/BuilderPanel';
import { BuilderReviewBar } from '@/components/reasoning/BuilderReviewBar';
import { BuilderSplit } from '@/components/reasoning/BuilderSplit';
import { HeroChatInput } from '@/components/reasoning/HeroChatInput';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  BUILDER_ENTITY_ID_PARAM,
  BUILDER_ENTITY_TYPE_PARAM,
} from '@/lib/crm/hooks/useBuilderMode';
import type { PreviewOverlay } from '@/lib/crm/preview';
import { getBuilderPlaceholder } from '@/lib/reasoning/builderPlaceholder';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { useNewConversationComposer } from '@/pages/reasoning/useNewConversationComposer';

import { useOrg } from '../../contexts/OrgContext';
import { useTitle } from '../../hooks/useTitle';

// No conversation exists on this route, so there is never a proposal to
// project. The panel draws the type's shape from its declared type alone.
const NO_OVERLAYS: Record<string, PreviewOverlay> = {};

export default function BuilderChat() {
  useTitle('Build automation');

  const { orgId } = useParams();
  const { currentOrg } = useOrg();
  const [searchParams] = useSearchParams();
  // The pre-conversation window: no conversation exists yet, so the subject
  // comes from the query string. It is handed to the server on the first send
  // and never read from the URL again.
  const builderEntityType = searchParams.get(BUILDER_ENTITY_TYPE_PARAM);
  const builderEntityId = searchParams.get(BUILDER_ENTITY_ID_PARAM);
  // A record in the URL means the user came here to change something that
  // already exists; without one they are describing something from nothing.
  // No memo — a string is compared by value, so it costs nothing downstream.
  const composerPlaceholder = getBuilderPlaceholder({
    entityTypeName: builderEntityType,
    hasSubject: Boolean(builderEntityId),
  });
  const { width: panelWidth, isResizing: isPanelResizing } = useSlideoutWidth();
  // Same breakpoint BuilderSplit uses to pick its mobile layout — keep the two
  // in step, or the review bar and the panel disagree about which one is on.
  const isLargeScreen = useBreakpoint('lg');
  // Mobile: the record panel opens full screen over the chat, closed by
  // default so the conversation is what the user lands in.
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const openMobilePanel = useCallback(() => setIsMobilePanelOpen(true), []);
  const closeMobilePanel = useCallback(() => setIsMobilePanelOpen(false), []);
  // Names what the panel holds, for "View automation".
  const recordLabel = builderEntityType
    ? getEntityTypeLabel(builderEntityType).toLowerCase()
    : 'record';
  const {
    message,
    setMessage,
    uploadedFiles,
    setUploadedFiles,
    submitting,
    error,
    handleSubmit,
  } = useNewConversationComposer();

  const orgIdValue = orgId || currentOrg?.id || '';
  let parsedOrgId: number | undefined;
  if (!orgIdValue) {
    parsedOrgId = undefined;
  } else if (typeof orgIdValue === 'number') {
    parsedOrgId = orgIdValue;
  } else {
    parsedOrgId = parseInt(orgIdValue, 10);
  }

  return (
    <div className='h-full gradient-bg'>
      {/* Mobile: the record panel opens full screen over the chat. Rendered
          outside BuilderSplit so it covers the header too — while open, the
          panel is the surface, not a section of the chat. */}
      {!isLargeScreen && isMobilePanelOpen && (
        <div className='fixed inset-0 z-40 flex flex-col bg-bg-primary lg:hidden'>
          <BuilderPanel
            orgId={parsedOrgId}
            entityTypeName={builderEntityType ?? undefined}
            entityId={builderEntityId ?? undefined}
            overlays={NO_OVERLAYS}
            draft={undefined}
            onClosePanel={closeMobilePanel}
          />
        </div>
      )}
      <BuilderSplit
        panelWidth={panelWidth}
        isPanelVisible
        isPanelResizing={isPanelResizing}
        panel={
          // No conversation exists on this route, so there is never a draft
          // yet — state S0 (creating) or S4-without-changes (editing). The
          // panel renders the type's shape or the real record accordingly.
          <BuilderPanel
            orgId={parsedOrgId}
            entityTypeName={builderEntityType ?? undefined}
            entityId={builderEntityId ?? undefined}
            overlays={NO_OVERLAYS}
            draft={undefined}
          />
        }
      >
        {/* The header exists before the conversation does — otherwise
            entering the builder and changing your mind leaves no way back
            except the browser button. Shown at every width: on mobile the
            chat is the full-screen primary surface, so this is the page
            header. */}
        <div className='flex h-full flex-col'>
          <BuilderChatHeader
            orgId={orgIdValue}
            entityTypeName={builderEntityType}
            entityId={builderEntityId ?? undefined}
            lookupOrgId={parsedOrgId}
          />
          <div className='flex flex-1 flex-col justify-end px-4 pb-6'>
            {/* Mobile only. On the EDIT path a record already exists, so there
                is something worth opening before a single message is sent —
                and with the chat full screen this is the only route to it.
                On the create path it renders nothing: the panel would be
                empty. */}
            {!isLargeScreen && (
              <div className='w-full max-w-3xl mx-auto'>
                <BuilderReviewBar
                  pendingCount={0}
                  hasRecord={Boolean(builderEntityId)}
                  recordLabel={recordLabel}
                  onReview={openMobilePanel}
                />
              </div>
            )}
            <div className='w-full max-w-3xl mx-auto'>
              {/* MOBILE gets the compact composer — the same ConversationInput
                  the proposal side panel and email composer use. Desktop keeps
                  the hero box.

                  `enableVoice` is passed explicitly on purpose:
                  ConversationInput defaults it to `variant !== 'simple'`, so
                  omitting it would silently remove voice input on mobile. */}
              {isLargeScreen ? (
                <HeroChatInput
                  value={message}
                  onChange={setMessage}
                  onSubmit={handleSubmit}
                  placeholder={composerPlaceholder}
                  loading={submitting}
                  error={error}
                  enableVoice={true}
                  autoFocus
                  orgId={parsedOrgId}
                  uploadedFiles={uploadedFiles}
                  onFilesChange={setUploadedFiles}
                />
              ) : (
                <ConversationInput
                  value={message}
                  onChange={setMessage}
                  onSubmit={handleSubmit}
                  placeholder={composerPlaceholder}
                  loading={submitting}
                  error={error}
                  enableVoice={true}
                  autoFocus
                  orgId={parsedOrgId}
                  uploadedFiles={uploadedFiles}
                  onFilesChange={setUploadedFiles}
                  variant='simple'
                  sticky={false}
                  compactUntilWrap
                />
              )}
            </div>
          </div>
        </div>
      </BuilderSplit>
    </div>
  );
}
